import { createSlice } from "@reduxjs/toolkit";
import { loadJSON, saveJSON, isHydrating } from "../../utils/gamePersistence";

// Default initial state
const DEFAULT_INITIAL_STATE = {
  open: false,
  fullscreen: false,
  clueBoardStyle: "pinboard",
  language: "English",
  masterVolume: 100,
  sfxVolume: 100,
  voiceNarration: true,
  accessibility: {
    colorBlindMode: false,
    darkMode: false,
  },
};

// Load initial state from localStorage (server snapshot may restore before store initialization)
const initialState = loadJSON("settings", DEFAULT_INITIAL_STATE);

// Function to apply the theme
const applyTheme = (settings) => {
  let theme = "color"; // Default theme
  if (
    settings.accessibility.darkMode &&
    !settings.accessibility.colorBlindMode
  ) {
    theme = "dark";
  } else if (
    settings.accessibility.colorBlindMode &&
    !settings.accessibility.darkMode
  ) {
    theme = "color-blind";
  } else if (
    settings.accessibility.darkMode &&
    settings.accessibility.colorBlindMode
  ) {
    theme = "color-blind-dark";
  }

  document.body.setAttribute("data-theme", theme);
};

// Function to apply fullscreen
const applyFullScreen = (settings) => {
  if (settings.fullscreen) {
    document.body.classList.add("full-screen");
  } else {
    document.body.classList.remove("full-screen");
  }
};

// Apply theme and fullscreen on initial load
applyTheme(initialState);
applyFullScreen(initialState);

// Persist settings to localStorage (respects hydration to prevent clobbering server snapshot)
const persist = (state) => {
  if (!isHydrating()) {
    saveJSON("settings", state);
  }
};

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    /** Replace the entire settings state from server snapshot */
    replaceSettingsState(state, action) {
      const next = action.payload;
      if (!next || typeof next !== "object") return state;

      // Apply the new state
      Object.keys(next).forEach((key) => {
        state[key] = next[key];
      });

      // Apply theme and fullscreen from restored settings
      applyTheme(state);
      applyFullScreen(state);

      return state;
    },

    toggleFullscreen(state) {
      state.fullscreen = !state.fullscreen;
      persist(state);
      applyFullScreen(state);

      // Request or exit actual fullscreen
      if (state.fullscreen) {
        if (document.documentElement.requestFullscreen) {
          document.documentElement.requestFullscreen().catch((err) => {
            console.error("Failed to enter fullscreen:", err);
          });
        }
      } else {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch((err) => {
            console.error("Failed to exit fullscreen:", err);
          });
        }
      }
    },

    toggleVoiceNarration(state) {
      state.voiceNarration = !state.voiceNarration;
      persist(state);
    },

    setFullscreenState(state, action) {
      state.fullscreen = action.payload;
      persist(state);
      applyFullScreen(state);
    },

    setClueBoardStyle(state, action) {
      if (state.clueBoardStyle !== action.payload) {
        state.clueBoardStyle = action.payload;
      } else {
        // If it's already the selected style, switch to the other one
        state.clueBoardStyle =
          action.payload === "pinboard" ? "whiteboard" : "pinboard";
      }
      persist(state);
    },

    setLanguage(state, action) {
      state.language = action.payload;
      persist(state);
    },

    setMasterVolume(state, action) {
      state.masterVolume = action.payload;
      persist(state);
    },

    setSfxVolume(state, action) {
      state.sfxVolume = action.payload;
      persist(state);
    },

    toggleColorBlindMode(state) {
      state.accessibility.colorBlindMode = !state.accessibility.colorBlindMode;
      persist(state);
      applyTheme(state);
    },

    toggleDarkMode(state) {
      state.accessibility.darkMode = !state.accessibility.darkMode;
      persist(state);
      applyTheme(state);
    },

    resetToDefaults(state) {
      // Completely replace the state with the default initial state
      Object.keys(DEFAULT_INITIAL_STATE).forEach((key) => {
        state[key] = DEFAULT_INITIAL_STATE[key];
      });

      // Remove from localStorage only if not hydrating
      if (!isHydrating()) {
        try {
          localStorage.removeItem("settings");
        } catch {}
      }

      // Apply default theme and fullscreen settings
      applyTheme(DEFAULT_INITIAL_STATE);
      applyFullScreen(DEFAULT_INITIAL_STATE);

      persist(state);
    },
  },
});

export const {
  setLanguage,
  setSfxVolume,
  toggleDarkMode,
  resetToDefaults,
  setMasterVolume,
  toggleFullscreen,
  setClueBoardStyle,
  setFullscreenState,
  toggleVoiceNarration,
  toggleColorBlindMode,
  replaceSettingsState,
} = settingsSlice.actions;

export default settingsSlice.reducer;
